import React, { useGlobal, useState } from "reactn"
import { graphql } from "gatsby"
import DefaultLayout from "../layouts/DefaultLayout"
import Wrapper from "../components/Wrapper"
import Image, { ImageProps } from "../components/Image"
import Slideshow from "../components/Slideshow"
import ApartmentPicker from "../components/ApartmentPicker"
import Apartments from "../components/Apartments"
import FinnLink from "../components/FinnLink"
import { FluidObject } from "gatsby-image"
import convert from "../utils/convert"

interface ProsjektetProps {
  image: ImageProps
  title: string
  text: string
}
interface SoveromProps {
  image: ImageProps
  caption: string
}
interface StandarderOgKvaliteterProps {
  title: string
  text: string
}
interface StueProps {
  image: ImageProps
  caption: string
}
interface EgenskaperProps {
  title: string
  text: string
  image: ImageProps
}
interface PenthouseImageProps extends ImageProps {
  caption: string
}
interface PenthouseProps {
  shown: boolean
  title: string
  text1: string
  text2: string
  images: PenthouseImageProps[]
}
interface KjokkenProps {
  title: string
  text: string
  image: ImageProps
}
interface BaderomProps {
  title: string
  text: string
  image: ImageProps
}
interface OmradetProps {
  title: string
  images: ImageProps[]
}
interface BeliggenhetProps {
  title: string
  text: string
}
interface KontaktProps {
  title: string
  text: string
  image: ImageProps
}

interface IndexPageTemplateProps {
  prosjektet: ProsjektetProps
  soverom: SoveromProps
  standarderOgKvaliteter: StandarderOgKvaliteterProps
  stue: StueProps
  egenskaper: EgenskaperProps[]
  penthouse: PenthouseProps
  kjokken: KjokkenProps
  baderom: BaderomProps
  omradet: OmradetProps
  beliggenhet: BeliggenhetProps
  kontakt: KontaktProps
  apartmentPickerViewBox: string
  apartmentPickerImg: {
    childImageSharp: {
      fluid: FluidObject
    }
  }
  finnHref: string
}

const IndexPageTemplate: React.FC<IndexPageTemplateProps> = ({
  prosjektet,
  soverom,
  standarderOgKvaliteter,
  stue,
  egenskaper,
  penthouse,
  kjokken,
  baderom,
  omradet,
  beliggenhet,
  kontakt,
  apartmentPickerViewBox,
  apartmentPickerImg,
  finnHref
}) => {
  const { 1: setSidebarIsOpen } = useGlobal<any>("sidebarIsOpen")
  const [ showMap, setShowMap ] = useState(false)

  return (
    <main id="index-page">
      <section
        id="om-prosjektet"
        className="pb-16 relative"
      >
        <div
          className="bg-cover bg-bottom flex justify-center text-white px-3 py-32"
          style={{
            backgroundImage: `url("${prosjektet.image.src.childImageSharp.fluid.src}")`
          }}
        >
          <Wrapper
            size="m"
            className="m-auto text-center py-32 md:py-0"
          >
            <h1 className="text-5xl font-bold tracking-tight lg:text-4xl">
              {prosjektet.title}
            </h1>
          </Wrapper>
        </div>

        <div className="px-6">
          <Wrapper
            size="s"
            className="mx-auto text-center"
          >
            <hr className="bg-accent h-px pt-px w-10 my-10 mx-auto" />

            <div
              dangerouslySetInnerHTML={{
                __html: convert(prosjektet.text)
              }}
            />
          </Wrapper>

          {finnHref && (
            <div className="flex justify-center mt-24 xl:mt-16">
              <FinnLink href={finnHref} />
            </div>
          )}
        </div>
      </section>

      <section
        id="soverom"
        className="py-16"
      >
        <figure className="ml-auto w-10/12 lg:w-full">
          <Image
            src={soverom.image.src}
            alt={soverom.image.alt}
          />

          <figcaption className="my-2 lg:mx-6">
            {soverom.caption}
          </figcaption>
        </figure>
      </section>

      <section
        id="standarder-og-kvaliteter"
        className="px-6 py-16"
      >
        <Wrapper
          size="m"
          className="mx-auto"
        >
          <h2 className="accent-left text-3xl font-bold tracking-tight mb-10 sm:text-2xl">
            {standarderOgKvaliteter.title}
          </h2>

          <div
            className="cc-2 drop-caps"
            dangerouslySetInnerHTML={{
              __html: convert(standarderOgKvaliteter.text)
            }}
          />
        </Wrapper>
      </section>

      <section
        id="stue"
        className="py-16"
      >
        <figure className="mr-auto w-10/12 lg:w-full">
          <Image
            src={stue.image.src}
            alt={stue.image.alt}
          />

          <figcaption className="my-2 text-right lg:mx-6">
            {stue.caption}
          </figcaption>
        </figure>
      </section>

      <section
        id="egenskaper"
        className="px-3 py-10"
      >
        <Wrapper
          size="l"
          className="mx-auto flex flex-wrap"
        >
          {egenskaper.map(({ image, title, text }, index) => (
            <div
              className="w-1/3 px-6 my-6 lg:px-3 md:w-1/2 xs:w-full"
              key={index}
            >
              <Image
                style={{
                  filter: "invert(0.5) sepia(1) saturate(5) hue-rotate(-60deg)"
                }}
                className="w-16"
                src={image.src}
                alt={image.alt}
              />

              <h3 className="text-xl font-semibold my-6">
                {title}
              </h3>

              <p>
                {text}
              </p>
            </div>
          ))}
        </Wrapper>
      </section>

      <section
        id="leilighetene"
        className="py-16"
      >
        <h2 className="mx-auto text-center text-3xl font-bold tracking-tight sm:text-2xl">
          Velg en etasje i Eugenies gate 5
        </h2>

        <hr className="bg-accent h-px pt-px w-10 mx-auto my-10" />

        <ApartmentPicker
          viewBox={apartmentPickerViewBox}
          backgroundImage={apartmentPickerImg}
        />

        <div className="mt-10">
          <Apartments />
        </div>
      </section>

      {penthouse.shown && (
        <section
          id="penthouse"
          className="px-6 py-16"
        >
          <Wrapper
            size="xl"
            className="mx-auto"
          >
            <Wrapper
              size="l"
              className="mx-auto"
            >
              <h2 className="accent-left text-3xl font-bold tracking-tight sm:text-2xl mb-10">
                {penthouse.title}
              </h2>

              <div
                className="cc-2 drop-caps"
                dangerouslySetInnerHTML={{
                  __html: convert(penthouse.text1)
                }}
              />
            </Wrapper>

            <Slideshow className="my-10">
              {penthouse.images.map((image, index) => (
                <figure key={index}>
                  <Image
                    src={image.src}
                    alt={image.alt}
                  />

                  <figcaption className="my-2 lg:mx-6">
                    {image.caption}
                  </figcaption>
                </figure>
              ))}
            </Slideshow>

            <Wrapper
              size="l"
              className="mx-auto"
            >
              <div
                className="cc-2"
                dangerouslySetInnerHTML={{
                  __html: convert(penthouse.text2)
                }}
              />
            </Wrapper>
          </Wrapper>
        </section>
      )}

      <section
        id="kjokken"
        className="pt-16"
      >
        <div className="flex items-center lg:flex-col-reverse">
          <div className="flex-1 px-6">
            <Wrapper
              size="s"
              className="mx-auto my-12 lg:max-w-full px-20 lg:px-0"
            >
              <Wrapper size="xs">
                <h2 className="text-3xl font-bold tracking-tight sm:text-2xl">
                  {kjokken.title}
                </h2>
              </Wrapper>

              <hr className="bg-accent h-px pt-px w-10 my-10" />

              <div
                dangerouslySetInnerHTML={{
                  __html: convert(kjokken.text)
                }}
              />
            </Wrapper>
          </div>

          <Image
            className="w-1/2 h-screen-80 lg:w-full lg:h-full"
            src={kjokken.image.src}
            alt={kjokken.image.alt}
            imgStyle={{
              objectPosition: "left"
            }}
          />
        </div>
      </section>

      <section
        id="baderom"
        className="pb-16"
      >
        <div className="flex items-center lg:flex-col">
          <Image
            className="w-1/2 h-screen-80 lg:w-full lg:h-full"
            src={baderom.image.src}
            alt={baderom.image.alt}
          />

          <div className="flex-1 px-6">
            <Wrapper
              size="s"
              className="mx-auto my-12 px-20 lg:px-0"
            >
              <Wrapper size="xs">
                <h2 className="text-3xl font-bold tracking-tight sm:text-2xl">
                  {baderom.title}
                </h2>
              </Wrapper>

              <hr className="bg-accent h-px pt-px w-10 my-10" />

              <div
                dangerouslySetInnerHTML={{
                  __html: convert(baderom.text)
                }}
              />
            </Wrapper>
          </div>
        </div>
      </section>

      <section
        id="omradet"
        className="py-16"
      >
        <Wrapper
          size="m"
          className="mx-auto text-center px-16 sm:px-6"
        >
          <h2 className="text-3xl font-bold tracking-tight sm:text-2xl mb-0">
            {omradet.title}
          </h2>

          <button
            className="my-10 mx-auto border-pink-500 border-2 border-solid text-accent rounded-sm px-10 py-2 shadow-xl hover:bg-pink-500 hover:text-white"
            onClick={() => { setShowMap(true) }}
          >
            Se på kartet
          </button>
        </Wrapper>

        <Slideshow
          options={{
            loop: true
          }}
        >
          {omradet.images.map((image, index) => (
            <figure key={index}>
              <Image
                src={image.src}
                alt={image.alt}
              />
            </figure>
          ))}
        </Slideshow>
      </section>

      <section
        id="beliggenhet"
        className="px-6 py-16"
      >
        <Wrapper
          size="m"
          className="mx-auto"
        >
          <Wrapper
            size="s"
            className="mb-10"
          >
            <h2 className="accent-left text-3xl font-bold tracking-tight sm:text-2xl">
              {beliggenhet.title}
            </h2>
          </Wrapper>

          <div
            className="cc-2 drop-caps"
            dangerouslySetInnerHTML={{
              __html: convert(beliggenhet.text)
            }}
          />
        </Wrapper>
      </section>

      <section
        id="kontakt-oss"
        className="pt-16"
      >
        <div
          className="bg-cover bg-center flex"
          style={{
            backgroundImage: `url("${kontakt.image.src.childImageSharp.fluid.src}")`,
            minHeight: "75vh"
          }}
        >
          <div
            className="flex flex-1 flex-col justify-center text-white px-6 py-12"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.66)" }}
          >
            <Wrapper
              size="m"
              className="mx-auto text-center px-16 sm:px-0"
            >
              <h2 className="text-3xl font-bold tracking-tight sm:text-2xl mb-0">
                {kontakt.title}
              </h2>
            </Wrapper>

            <hr className="bg-accent h-px pt-px w-10 my-10 mx-auto" />

            <Wrapper
              size="s"
              className="mx-auto mb-10 text-center break-words"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: convert(kontakt.text)
                }}
              />
            </Wrapper>

            <div className="mx-auto my-4">
              <button
                className="bg-pink-500 border-pink-500 border-2 border-solid text-white px-10 py-2 hover:bg-pink-900 hover:border-pink-900 rounded-sm shadow-xl sm:px-6 xs:px-2"
                onClick={() => { setSidebarIsOpen(true) }}
              >
                Meld interesse
              </button>
            </div>
          </div>
        </div>
      </section>

      {showMap && (
        <div
          className="fixed z-10 inset-0 w-full h-full overflow-y-auto flex"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.66)" }}
          onClick={() => { setShowMap(false) }}
        >
          <div
            className="m-auto shadow bg-white w-full"
            style={{ maxWidth: 600 }}
            onClick={(e) => {
              e.stopPropagation()
              e.nativeEvent.stopImmediatePropagation()
            }}
          >
            <div className="py-3 px-6 flex items-center justify-between text-black">
              <h2 className="font-bold text-lg mb-0">
                Eugenies Gate 5 på kartet
              </h2>
              <button
                onClick={() => setShowMap(false)}
                className="text-3xl leading-none"
              >
                &times;
              </button>
            </div>

            <div
              style={{
                background: "url('https://i.redd.it/o6m7b0l6h6pz.gif') center center no-repeat"
              }}
            >
              <iframe
                width="100%"
                height="543"
                frameBorder="0"
                scrolling="no"
                marginHeight={0}
                marginWidth={0}
                src="https://maps.google.com/maps?width=100%&amp;height=543&amp;hl=en&amp;coord=59.927460,10.733310&amp;q=Eugenies%20Gate%205%2C%20oslo+(Eugenies%20Gate%205)&amp;ie=UTF8&amp;t=&amp;z=13&amp;iwloc=B&amp;output=embed"
              />
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

interface IndexPageProps {
  data: {
    markdownRemark: {
      frontmatter: IndexPageTemplateProps
    }
  }
}

const IndexPage: React.FC<IndexPageProps> = ({ data }) => {
  const { frontmatter } = data.markdownRemark
  return (
    <DefaultLayout>
      <IndexPageTemplate
        prosjektet={frontmatter.prosjektet}
        soverom={frontmatter.soverom}
        standarderOgKvaliteter={frontmatter.standarderOgKvaliteter}
        stue={frontmatter.stue}
        egenskaper={frontmatter.egenskaper}
        penthouse={frontmatter.penthouse}
        kjokken={frontmatter.kjokken}
        baderom={frontmatter.baderom}
        omradet={frontmatter.omradet}
        beliggenhet={frontmatter.beliggenhet}
        kontakt={frontmatter.kontakt}
        apartmentPickerViewBox={frontmatter.apartmentPickerViewBox}
        apartmentPickerImg={frontmatter.apartmentPickerImg}
        finnHref={frontmatter.finnHref}
      />
    </DefaultLayout>
  )
}

export default IndexPage

export const indexPageQuery = graphql`
  query IndexPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        prosjektet {
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
          title
          text
        }
        soverom {
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
          caption
        }
        standarderOgKvaliteter {
          title
          text
        }
        stue {
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
          caption
        }
        egenskaper {
          title
          text
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
        }
        penthouse {
          shown
          title
          text1
          text2
          images {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
        }
        kjokken {
          title
          text
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
        }
        baderom {
          title
          text
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
        }
        omradet {
          title
          images {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
        }
        beliggenhet {
          title
          text
        }
        kontakt {
          image {
            src {
              childImageSharp {
                fluid(maxWidth: 1920 quality: 100) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            alt
          }
          title
          text
        }
        apartmentPickerViewBox
        apartmentPickerImg {
          childImageSharp {
            fluid(maxWidth: 1920 quality: 100) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        finnHref
      }
    }
  }
`
